import React, { useState, useEffect } from 'react';
import FileCard from '../components/FileCard';
import FileDownloadForm from '../components/FileDownloadForm';
import './Registrations.css';

const RegistrationsPage = () => {
  const [files, setFiles] = useState([]);
  const [fileUploadLimit, setFileUploadLimit] = useState(25 * 1024 * 1024); // 25MB
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewDocTypeId, setPreviewDocTypeId] = useState(null);
  const [nonCurrentAndOtherFiles, setNonCurrentAndOtherFiles] = useState([]);
  const [showNonCurrentAndOtherFiles, setShowNonCurrentAndOtherFiles] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  const [editDocumentNumber, setEditDocumentNumber] = useState('');
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editDocumentNumberLabel, setEditDocumentNumberLabel] = useState('');

  // Custom categories state
  const [customCategories, setCustomCategories] = useState([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');

  // Per-category expand/collapse (default collapsed)
  const [expandedSections, setExpandedSections] = useState({});
  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  const documentTypes = [
    {
      id: 'EIN',
      label: 'Employer Identification Number (EIN)',
    },
    {
      id: 'UEI',
      label: 'Unique Entity Identifier (UEI)',
    },
    {
      id: 'SBA',
      label: 'Small Business Administration (SBA)',
    },
    {
      id: 'COF',
      label: 'Certificate of Formation',
    },
    {
      id: 'CAGE',
      label: 'CAGE',
    },
  ];

  // Merge fixed document types with custom categories
  const allDocumentTypes = [
    ...documentTypes,
    ...customCategories.map(c => ({
      id: c.category || c.name,
      label: c.name,
      isCustom: true,
      categoryId: c.id,
    }))
  ];

  const handleUpdateDocumentNumber = async (documentName, newDocumentNumber) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/update-document-number`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: documentName,
          documentNumber: newDocumentNumber
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update document number: ${response.statusText}`);
      }

      // Update the local state to reflect the change
      setFiles(prevFiles => prevFiles.map(file =>
        file.documentName === documentName
          ? { ...file, documentNumber: newDocumentNumber }
          : file
      ));

      // Clear editing state
      setEditingDocumentId(null);
      alert('Document number updated successfully!');
    } catch (error) {
      console.error('Error updating document number:', error);
      alert('Error updating document number.');
    }
  };

  // Update this function in your Registrations.js file
  const handleUpdateDocumentName = async (currentDocumentName, newDocumentName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/update-document-name`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentFileName: currentDocumentName,
          newFileName: newDocumentName
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update document name: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();

      // Update the local state to reflect the change
      setFiles(prevFiles => prevFiles.map(file =>
        file.documentName === currentDocumentName
          ? { ...file, documentName: newDocumentName }
          : file
      ));

      // Also update non-current files if necessary
      setNonCurrentAndOtherFiles(prevFiles =>
        prevFiles.map(file =>
          file.documentName === currentDocumentName
            ? { ...file, documentName: newDocumentName }
            : file
        )
      );

      // If the file was being previewed, update the preview URL
      if (previewUrl && previewUrl.includes(currentDocumentName)) {
        setPreviewUrl(''); setPreviewDocTypeId(null);
        // You may want to re-fetch the preview for the new name
        setTimeout(() => handlePreview(newDocumentName), 500);
      }

      alert('Document name updated successfully!');
    } catch (error) {
      console.error('Error updating document name:', error);
      alert(`Error updating document name: ${error.message}`);
    }
  };

  const handleUpdateDocumentNumberLabel = async (documentName, newDocumentNumberLabel) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/update-document-number-label`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: documentName,
          documentNumberLabel: newDocumentNumberLabel
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update document number label: ${response.statusText}`);
      }

      // Update the local state to reflect the change
      setFiles(prevFiles => prevFiles.map(file =>
        file.documentName === documentName
          ? { ...file, documentNumberLabel: newDocumentNumberLabel }
          : file
      ));

      // Clear editing state
      setEditingLabelId(null);
      alert('Document number label updated successfully!');
    } catch (error) {
      console.error('Error updating document number label:', error);
      alert('Error updating document number label. Please try again.');
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/existing-files`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Fetched files:', data);
      setFiles(data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchCustomCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/registration-categories`, {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCustomCategories(data);
      }
    } catch (error) {
      console.error('Error fetching custom categories:', error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Category name is required');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/registration-categories`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newCategoryName.trim() }),
      });
      if (response.ok) {
        const data = await response.json();
        setCustomCategories(prev => [...prev, data.category]);
        setShowAddCategory(false);
        setNewCategoryName('');
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to create category');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Failed to create category');
    }
  };

  const handleUpdateCategory = async (categoryId, name) => {
    if (!name.trim()) {
      alert('Category name is required');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/registration-categories/${categoryId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (response.ok) {
        const data = await response.json();
        setCustomCategories(prev => prev.map(c => (c.id === categoryId ? data.category : c)));
        setEditingCategoryId(null);
        setEditCategoryName('');
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this category? This action cannot be undone.'
    );
    if (!confirmDelete) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/registration-categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        setCustomCategories(prev => prev.filter(c => c.id !== categoryId));
      } else {
        alert('Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchNonCurrentAndOtherFiles();
    fetchCustomCategories();
  }, []);

  const fetchNonCurrentAndOtherFiles = async () => {
    try {
      console.log('Fetching non-current and other files...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/non-current-and-other-files`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      // Filter out files with "N/A" documentName
      const filteredData = data.filter(file => file.documentName !== 'N/A' && file.documentName !== '');
      setNonCurrentAndOtherFiles(filteredData);
      console.log('Fetched Non-Current and Other Files:', filteredData);
    } catch (error) {
      console.error('Error fetching non-current and other files:', error);
    }
  };


  useEffect(() => {

    if (showNonCurrentAndOtherFiles) {
      fetchNonCurrentAndOtherFiles();
    }
  }, [showNonCurrentAndOtherFiles]);



  const handleUpload = async ({ file, documentType, documentNumber }) => {
    try {
        // Handle number-only submission (no file)
        if (!file && documentNumber) {
            const fileName = getFileNameFromDocumentType(documentType, '');
            await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/update-metadata`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ fileName, documentType, documentNumber, contentType: '', numberOnly: true })
            });

            alert('Registration number saved successfully!');

            // Fetch the updated list of existing files
            await fetchFiles();
            await fetchNonCurrentAndOtherFiles();
            return;
        }

        // Handle file upload with optional number
        if (file.size > fileUploadLimit) {
            alert(`File size exceeds the limit of ${fileUploadLimit / (1024 * 1024)} MB.`);
            return;
        }

        const fileName = getFileNameFromDocumentType(documentType, file.name);
        const contentType = file.type || 'application/octet-stream';
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-upload-url`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ fileName, documentType, documentNumber, contentType }),
        });
        const { url } = await response.json();

        const uploadResponse = await fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': contentType,
            },
            body: file,
        });

        if (uploadResponse.ok) {
            // Only call update-metadata if the upload was successful
            await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/update-metadata`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ fileName, documentType, documentNumber, contentType })
            });


            alert('File uploaded successfully!');

            // Fetch the updated list of existing files
            await fetchFiles();
            await fetchNonCurrentAndOtherFiles();

        } else {
            alert('Failed to upload the file.');
        }
    } catch (error) {
        console.error('Error uploading file:', error);
        alert('Error uploading file.');
    }
  };



  const handleDownload = async (documentName) => {
    console.log("downloading");
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-download-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileName: documentName }),
      });
      const { url } = await response.json();

      console.log('Downloading file:', url);

      // Create a link and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documentName); // This attribute prompts download
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file.');
    }
  };

  const handlePreview = async (documentName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-preview-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileName: documentName }),
      });
      const { url } = await response.json();

      // Triggering preview
    //   window.open(url, '_blank'); // previewing in a different window
    setPreviewUrl(url);
    setPreviewDocTypeId(documentName);
    } catch (error) {
      console.error('Error previewing file:', error);
      alert('Error previewing file.');
    }
  };

  const handleDelete = async (documentName) => {
    // Ask for confirmation before deletion
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this file? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return; // User canceled the deletion
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/delete`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileName: documentName }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.statusText}`);
      }

      // Update the UI by removing the deleted file
      setFiles(prevFiles => prevFiles.filter(file => file.documentName !== documentName));

      // Also update non-current files if necessary
      setNonCurrentAndOtherFiles(prevFiles =>
        prevFiles.filter(file => file.documentName !== documentName)
      );

      alert('File deleted successfully!');

      // If the preview URL was for this file, clear it
      if (previewUrl && previewUrl.includes(documentName)) {
        setPreviewUrl(''); setPreviewDocTypeId(null);
      }

      // If we were editing this document's number, clear the editing state
      if (editingDocumentId && files.find(f => f.documentName === documentName)?.documentType === editingDocumentId) {
        setEditingDocumentId(null);
      }

    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file. Please try again.');
    }
  };

  const getFileNameFromDocumentType = (documentType, originalFileName) => {
    if (documentType === 'OTH') {
      return originalFileName;
    }
    // Extract extension from original file name (if any)
    const lastDot = originalFileName ? originalFileName.lastIndexOf('.') : -1;
    const extension = lastDot > -1 ? originalFileName.substring(lastDot) : '';
    return `${documentType}${extension}`;
  };


  // Find the latest version of each document type
  const getLatestFile = (documentType) => {
    const relevantFiles = files.filter(file =>
      file.documentType === documentType ||
      file.documentName === documentType ||
      (file.documentName && file.documentName.startsWith(`${documentType}.`))
    );
    if (relevantFiles.length === 0) return null;

    return relevantFiles.reduce((latest, file) =>
      !latest || new Date(file.lastModified) > new Date(latest.lastModified) ? file : latest
    , null);
  };

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get the full document number column header for a given document type
  const getDocumentNumberHeader = (docType) => {
    switch (docType.id) {
      case 'EIN': return 'EIN';
      case 'UEI': return 'UEI #';
      case 'SBA': return 'SBC Control ID';
      case 'COF': return 'Date for Formation';
      case 'CAGE': return 'CAGE #';
      default: return 'Document #';
    }
  };



  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Registrations</h1>
        <p className="text-gray-600 mt-2">Upload and manage registration documents</p>
      </div>

      {/* Add Category Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowAddCategory(true)}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Add Category
        </button>
      </div>

      {/* Document Sections */}
      <div className="space-y-3">
        {allDocumentTypes.map(docType => {
          const latestFile = getLatestFile(docType.id);
          const showPreviewHere = previewUrl && previewDocTypeId && latestFile && previewDocTypeId === latestFile.documentName;
          const isCollapsed = !expandedSections[docType.id];
          return (
            <React.Fragment key={docType.id}>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Section Header */}
              <div className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50" onClick={() => toggleSection(docType.id)}>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm">{isCollapsed ? '▶' : '▼'}</span>
                  {docType.isCustom && editingCategoryId === docType.categoryId ? (
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                        value={editCategoryName}
                        onChange={(e) => setEditCategoryName(e.target.value)}
                        autoFocus
                      />
                      <button
                        onClick={() => handleUpdateCategory(docType.categoryId, editCategoryName)}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { setEditingCategoryId(null); setEditCategoryName(''); }}
                        className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <h3 className="text-lg font-semibold text-gray-900">{docType.label}</h3>
                  )}
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    latestFile ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {latestFile ? 'Uploaded' : 'Missing'}
                  </span>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {docType.isCustom && editingCategoryId !== docType.categoryId && (
                    <button
                      onClick={() => { setEditingCategoryId(docType.categoryId); setEditCategoryName(docType.label); }}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      title="Rename category"
                    >
                      Rename
                    </button>
                  )}
                  {docType.isCustom && (
                    <button
                      onClick={() => handleDeleteCategory(docType.categoryId)}
                      className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                      title="Delete category"
                    >
                      Delete
                    </button>
                  )}
                  <label className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                    {latestFile ? 'Replace' : 'Upload'}
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleUpload({ file: e.target.files[0], documentType: docType.id, documentNumber: latestFile?.documentNumber || '' });
                        }
                      }}
                    />
                  </label>
                </div>
              </div>

              {/* File Details */}
              {!isCollapsed && latestFile && (
                <div className="border-t border-gray-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {getDocumentNumberHeader(docType)}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="px-4 py-3 text-sm">
                            {editingDocumentId === docType.id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                                  value={editDocumentNumber}
                                  onChange={(e) => setEditDocumentNumber(e.target.value)}
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleUpdateDocumentNumber(latestFile.documentName, editDocumentNumber)}
                                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => setEditingDocumentId(null)}
                                  className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-gray-900">{latestFile.documentNumber || 'Not specified'}</span>
                                <button
                                  onClick={() => { setEditingDocumentId(docType.id); setEditDocumentNumber(latestFile.documentNumber || ''); }}
                                  className="p-1 text-gray-400 hover:text-blue-600"
                                  title="Edit"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(latestFile.lastModified)}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-3">
                              <button
                                onClick={() => handlePreview(latestFile.documentName)}
                                className="text-blue-600 hover:text-blue-700 text-sm"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleDownload(latestFile.documentName)}
                                className="text-blue-600 hover:text-blue-700 text-sm"
                              >
                                Download
                              </button>
                              <button
                                onClick={() => handleDelete(latestFile.documentName)}
                                className="text-red-600 hover:text-red-700 text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Inline Preview - shown right below the file whose View was clicked */}
            {showPreviewHere && (
              <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
                  <button onClick={() => { setPreviewUrl(''); setPreviewDocTypeId(null); setPreviewDocTypeId(null); }} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {['jpg', 'jpeg', 'png', 'gif'].includes(previewUrl.split('.').pop()?.split('?')[0]?.toLowerCase()) ? (
                  <img src={previewUrl} alt="Document Preview" className="max-w-full max-h-[500px] mx-auto" />
                ) : (
                  <iframe src={previewUrl} title="Document Preview" className="w-full h-[500px] border border-gray-200 rounded" />
                )}
              </div>
            )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Add Category Modal */}
      {showAddCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add New Category</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex space-x-2 pt-4">
                <button
                  onClick={handleAddCategory}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-1"
                >
                  Create Category
                </button>
                <button
                  onClick={() => {
                    setShowAddCategory(false);
                    setNewCategoryName('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RegistrationsPage;
